import React from "react";
import { motion } from "framer-motion";

const FrankChenTitleSlide = () => {
  return (
    <div
      className="h-full w-full relative overflow-hidden flex flex-col items-center justify-center font-sans"
      style={{
        background:
          "radial-gradient(1200px 700px at 50% -10%, rgba(37,99,235,0.25) 0%, rgba(15,23,42,0) 60%), linear-gradient(180deg, #020617 0%, #0b1224 40%, #0a1128 100%)",
      }}
    >
      {/* Ambient blur orbs */}
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 680,
          height: 680,
          top: "-200px",
          left: "-120px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.35) 0%, rgba(59,130,246,0) 65%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 620,
          height: 620,
          bottom: "-220px",
          right: "-140px",
          background:
            "radial-gradient(circle, rgba(99,102,241,0.3) 0%, rgba(99,102,241,0) 65%)",
          filter: "blur(60px)",
        }}
        animate={{ x: [0, -35, 0], y: [0, -25, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Grid mesh */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.22) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 85%)",
        }}
      />

      {/* Corner accents */}
      <div aria-hidden className="absolute top-10 left-10 w-20 h-20 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-blue-400/60 to-transparent" />
        <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-blue-400/60 to-transparent" />
      </div>
      <div aria-hidden className="absolute top-10 right-10 w-20 h-20 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-px bg-gradient-to-l from-blue-400/60 to-transparent" />
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-blue-400/60 to-transparent" />
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 w-20 h-20 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-blue-400/60 to-transparent" />
        <div className="absolute bottom-0 left-0 w-px h-full bg-gradient-to-t from-blue-400/60 to-transparent" />
      </div>
      <div aria-hidden className="absolute bottom-10 right-10 w-20 h-20 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-blue-400/60 to-transparent" />
        <div className="absolute bottom-0 right-0 w-px h-full bg-gradient-to-t from-blue-400/60 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-6xl">
        {/* Main title — Asha Health × Dr. Frank Chen */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          className="flex flex-wrap items-center justify-center gap-x-6 md:gap-x-10 gap-y-3 text-4xl md:text-6xl lg:text-[80px] font-bold tracking-tight text-white leading-[1.05]"
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-blue-200/90 drop-shadow-sm">
            Asha Health
          </span>

          {/* Multiplication symbol with glow */}
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
            className="relative inline-flex items-center justify-center"
          >
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(96,165,250,0.55) 0%, rgba(96,165,250,0) 70%)",
                filter: "blur(22px)",
              }}
              animate={{ opacity: [0.4, 0.9, 0.4], scale: [0.9, 1.2, 0.9] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <span className="relative text-blue-300/90 font-light leading-none">
              ×
            </span>
          </motion.span>

          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(90deg, #60a5fa 0%, #a5b4fc 50%, #60a5fa 100%)",
              backgroundSize: "200% 100%",
            }}
          >
            <motion.span
              className="inline-block bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #60a5fa 0%, #a5b4fc 50%, #60a5fa 100%)",
                backgroundSize: "200% 100%",
              }}
              animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            >
              Dr. Frank Chen
            </motion.span>
          </span>
        </motion.h1>

        {/* Quote valid through */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="mt-14 flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-slate-400 font-mono"
        >
          <div className="h-px w-10 bg-gradient-to-r from-transparent to-slate-500/60" />
          <span>
            <span className="text-slate-500">Quote valid till</span>{" "}
            <span className="text-slate-100 font-semibold tracking-wider">
              July 25, 2026
            </span>
          </span>
          <div className="h-px w-10 bg-gradient-to-l from-transparent to-slate-500/60" />
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(2,6,23,0.6) 100%)",
        }}
      />
    </div>
  );
};

export default FrankChenTitleSlide;
